/* eslint-disable max-len */
import {
  FormControlLabel, Radio, RadioGroup,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
import {
  useTheme
} from "@mui/material/styles";
import {
  Fragment,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  AdminBoxContainer, BackButton,
  DomainBox,
  LearnerBoxesContainer,
  LearnerBoxesList
} from "./SignupDomain.styles";
import {
  Icon, Modal,
  Spinner
} from "../../components";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { NAVBAR_ITEMS, PERMISSIONS } from "../../consts";
import { useNavigate } from "../../services";
import { useAuthStore, useNavbarStore } from "../../stores";
import { getEnvironmentVariables, getLastPath } from "../../utils/general";

const { environment } = getEnvironmentVariables();

export function SignupDomain() {
  const { t } = useTranslation();
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const authStoreIsLoading = useAuthStore(state => state.isLoading);
  const domains = useAuthStore(state => state.userInfo?.initiatives);
  const hasCorporate = useAuthStore(state => Boolean(state.userData?.initiative_id));
  const isAdmin = useAuthStore(state =>
    state.userData?.permissions?.some(permission => (
      permission === PERMISSIONS["admin.dashboard.access"]
    ))
  );
  const isLoading = useAuthStore(state => state.isLoading);
  const postInitiativeId = useAuthStore(state => state.postInitiativeId);
  const sessionToken = useAuthStore(state => state.sessionToken);
  const signOut = useAuthStore(state => state.signOut);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [goToAdmin, setGoToAdmin] = useState<boolean>(false);
  const isImpersonation = useAuthStore(state => state.isImpersonation);
  const { adminLink }= getEnvironmentVariables();
  const setNavbarItem = useNavbarStore(state => state.setNavbarItem);
  const primaryPage =  smartConfiguration && smartConfiguration?.visForyou === false ?
    NAVBAR_ITEMS.EXPLORE :  NAVBAR_ITEMS.FOR_YOU;
  const authenticationMode = useAuthStore(state => state.mode);
  const isOneInitiative = Array.isArray(domains) && domains.length === 1 && !isAdmin;
  const [selectedDomain, setSelectedDomain] = useState<string | null>();
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const showInitiativeEnrollmentWarning = useAuthStore(state => state.showInitiativeEnrollmentWarning);
  const setShowInitiativeEnrollmentWarning = useAuthStore(state => state.setShowInitiativeEnrollmentWarning);

  if(!isOneInitiative && !isSeedingSession){
    setSpinnerLoginVisible(false);
  }

  if(!isOneInitiative){
    document.cookie = `secure; expires=${new Date().toUTCString()}; domain=.${getLastPath()}; path=/`;
  }

  useEffect(()=> {
    const subDomainRedirect = !!(smartConfiguration?.urlDomain && smartConfiguration?.urlCorporateDomain);
    /* LOADING */

    if (isImpersonation) {
      setSpinnerLoginVisible(false);
      return;
    }

    if (hasCorporate || isLoading) {
      return;
    /* ADMIN NO INITIATIVES */
    } else if (Array.isArray(domains) && domains.length === 0 && isAdmin) {
      window.location.href = `${adminLink}?sessionId=${sessionToken}`;
    /* NO ADMIN SINGEL INITIATIVE */
    } else if (Array.isArray(domains) && domains.length === 1 && !isAdmin && !smartConfiguration) {
      // setSelectedDomain(domains[0].id);
      // handleConfirm();
      postInitiativeId(domains[0].id);
      setNavbarItem(primaryPage);
      setSpinnerLoginVisible(true);
    /* REDIRECT TO INITIATIVE SUB-DOMAIN */
    } else if (
      environment !== "LOCAL"
      && subDomainRedirect
    ) {
      setNavbarItem(primaryPage);
      setSpinnerLoginVisible(false);
    /* SELECT ADMIN OPTION */
    } else if (smartConfiguration && !subDomainRedirect && goToAdmin) {
      window.location.href = `${adminLink}?sessionId=${sessionToken}`;
    /* REDIRECT TO ROOT */
    } else if (smartConfiguration && !subDomainRedirect && !goToAdmin) {
      // navigate("/");
      navigate("/signup");
      setNavbarItem(primaryPage);
    }
  }, [
    adminLink,
    domains,
    goToAdmin,
    hasCorporate,
    isAdmin,
    isLoading,
    navigate,
    primaryPage,
    postInitiativeId,
    setSpinnerLoginVisible,
    sessionToken,
    setNavbarItem,
    smartConfiguration,
    authenticationMode]);

  useEffect(() => {
    if(selectedDomain) {
      setGoToAdmin(selectedDomain === "admin");
    }
  }, [selectedDomain]);

  const onConfirmDomain = async () => {

    if(!selectedDomain) return;

    if(selectedDomain === "admin") {
      window.location.href = `${adminLink}?sessionId=${sessionToken}&mode=${authenticationMode}&initiativeUrl=${window.location.host}`;
    } else {
      setSpinnerLoginVisible(true);
      await postInitiativeId(selectedDomain.toString());
    }
  };

  return isOneInitiative && !isImpersonation ?
    <Spinner/> : (
      <>
        <BackButton
          alignSelf="flex-start"
          color="info"
          onClick={ () => { signOut("/access/login") } }
          href={ "/" }
          startIcon={
            <Icon
              icon="arrow_left"
              color={ theme.customColors.backgroundPrimaryCta }
              size={ isMobile ? 15 : 20 }
            />
          }
          variant="text"
          $isMobile={ isMobile }
        >
          { t("back") }
        </BackButton>
        <Stack
          alignItems="center"
          gap={ isMobile ? 3 : 6 }
          width={ isMobile ? "100%" : "90%" }
        >
          { !authStoreIsLoading && domains?.length === 0 ? (
            <Typography
              fontSize={ isMobile ? "0.875rem" : "1.25rem" }
              lineHeight={ isMobile ? (9 / 7) : (21 / 20) }
              textAlign="center"
            >
              { isAdmin ? t("redirect_admin") : t("no_initiatives_available") }
            </Typography>
          ) : (
            <Fragment>
              <Stack
                alignItems="center"
                gap={ isMobile ? 1 : 2 }
              >
                <Typography
                  tabIndex={ 0 }
                  color={ theme.customColors.textPrimary }
                  fontSize={ isMobile ? "1.25rem" : "2rem" }
                  fontWeight={ 700 }
                  lineHeight={ isMobile ? (21 / 20) : (19 / 16) }
                  textAlign="center"
                >
                  { t("portal_selection") }
                </Typography>
                <Typography
                  tabIndex={ 0 }
                  fontSize={ isMobile ? "0.875rem" : "1.25rem" }
                  lineHeight={ isMobile ? (9 / 7) : (21 / 20) }
                  textAlign="center"
                >
                  { t("portal_continue") }
                </Typography>
              </Stack>
              {

                (hasCorporate && isLoading) ? (
                  <Spinner />
                ) : (
                  <RadioGroup
                    onChange={ (e) => setSelectedDomain(e.target.value) }
                    sx={ {
                      width: "100%",
                      "& svg":{
                        color:theme.customColors.accentVariantA
                      }
                    } }
                  >
                    { isAdmin && !isImpersonation && (
                      <AdminBoxContainer>
                        <Typography aria-hidden="true" sx={ { fontWeight: 500 } }>Admin</Typography>
                        <DomainBox tabIndex={ 0 } $isActive={ selectedDomain === "admin" } onClick={ () => setSelectedDomain("admin") }>
                          <FormControlLabel value={ "admin" } control={ <Radio /> } checked={ selectedDomain === "admin" } label={ t("go_to_admin") } />
                        </DomainBox>
                      </AdminBoxContainer>
                    ) }
                    <LearnerBoxesContainer >
                      <Typography aria-hidden="true" sx={ { fontWeight: 500 } }>Learner</Typography>
                      <LearnerBoxesList >
                        {
                          isLoading ? <Spinner /> :
                            domains?.map((domain) => (
                              <DomainBox tabIndex={ 0 } key={ `domain--${domain.id}` } $isActive={ domain.id.toString() === selectedDomain } onClick={ () => setSelectedDomain(domain.id.toString()) }>
                                <FormControlLabel value={ domain.id } control={ <Radio /> } checked={ domain.id.toString() === selectedDomain } label={ domain.label } />
                              </DomainBox>
                            ))
                        }
                      </LearnerBoxesList>
                    </LearnerBoxesContainer>
                  </RadioGroup>
                )
              }
              <ButtonDigited
                typology="primary"
                disabled={ !selectedDomain }
                id={ "selectDomainBtn" }
                onClick={ onConfirmDomain }
              >
                { t("continue") }
              </ButtonDigited>
            </Fragment>
          )
          }
        </Stack>
        <Modal
          description={ t("invalid_initiative") }
          onClose={ () => setShowInitiativeEnrollmentWarning(false) }
          open={ showInitiativeEnrollmentWarning }
          title={ t("attention") }
        />
      </>
    );
}
